import { locales } from "@opensea/navigation"
import type { MouseEvent, SyntheticEvent } from "react"

type WalletTrackingParameters = {
  chainId: number | undefined
  web3Wallet: string | undefined
  totalUsdBalance: number | undefined
  balanceByChain: Record<string, number> | undefined
  authenticated: boolean | undefined
}

let _walletTrackingParameters: WalletTrackingParameters = {
  chainId: undefined,
  web3Wallet: undefined,
  totalUsdBalance: undefined,
  balanceByChain: undefined,
  authenticated: undefined,
}

export function setWalletTrackingParameters(params: WalletTrackingParameters) {
  _walletTrackingParameters = params
}

export function getWalletTrackingParameters() {
  return _walletTrackingParameters
}

type ReferralTrackingParameters = {
  os_ref: string | undefined
}

let _referralTrackingParameters: ReferralTrackingParameters = {
  os_ref: undefined,
}

export function setReferralTrackingParameter(osRef: string | undefined) {
  _referralTrackingParameters = { os_ref: osRef }
}

export function getReferralTrackingParameters() {
  return _referralTrackingParameters.os_ref ? _referralTrackingParameters : {}
}

function stripLocaleFromPath(path: string) {
  for (const locale of locales) {
    const localeSegment = `/${locale}`
    if (path.startsWith(localeSegment)) {
      return path.slice(localeSegment.length) || "/"
    }
  }
  return path
}

function getPageFromPath(path: string) {
  const normalizedPath = stripLocaleFromPath(path)
  if (normalizedPath === "/") {
    return "Home Page"
  }
  if (normalizedPath.startsWith("/item/")) {
    return "Item Page"
  }
  if (normalizedPath.startsWith("/collection/")) {
    return "Collection Page"
  }
  if (normalizedPath.startsWith("/stats/")) {
    return "Stats Page"
  }
  if (normalizedPath.startsWith("/activity/")) {
    return "Activity Page"
  }
  if (normalizedPath.startsWith("/rewards")) {
    return "Leaderboard Page"
  }
  if (normalizedPath.startsWith("/token/")) {
    return "Token Page"
  }
  const pathParts = normalizedPath.split("/")
  const isVanityUrl = pathParts.length === 2 && pathParts[1].startsWith("0x")
  if (path.startsWith("/profile") || isVanityUrl) {
    return "Profile Page"
  }
  return undefined
}

type ExtractableEvent = Event | MouseEvent | FocusEvent | SyntheticEvent
type ExtractedProperties = Record<string, unknown>

export const extractProperties = (
  event?: ExtractableEvent,
): ExtractedProperties => {
  const properties: ExtractedProperties = {
    path: window.location.pathname,
    url: window.location.href,
    page: getPageFromPath(window.location.pathname),
  }

  // If no event, we're done with top-level properties
  if (!event) {
    return properties
  }

  // Some react / ui-kit elements fire SyntheticEvents, so we need to extract the native event
  if (isSyntheticEvent(event)) {
    const nativeEvent = event.nativeEvent
    return extractProperties(nativeEvent)
  }

  const target = event.target as HTMLElement | undefined

  if (event instanceof MouseEvent) {
    properties.clientX = event.clientX
    properties.clientY = event.clientY
    properties.pageX = event.pageX
    properties.pageY = event.pageY
  }

  if (event instanceof FocusEvent) {
    // TODO: Are there any interesting properties on focus event?
  }

  if (target instanceof Element) {
    // TODO: add xpath
    // properties.xpath = xpath
  }

  if (target?.tagName === "BUTTON") {
    const button = event.target as HTMLButtonElement
    properties.text = button.textContent
    properties.id = button.id
    properties.type = button.type
  }

  if (target?.tagName === "INPUT") {
    const input = event.target as HTMLInputElement
    properties.id = input.id
    properties.name = input.name
    properties.type = input.type

    // We probably don't want to send the value of the input field
    // properties.text = input.value
  }

  return properties
}

function isSyntheticEvent(event: ExtractableEvent): event is SyntheticEvent {
  return Boolean((event as SyntheticEvent).nativeEvent)
}
